import React, { Component } from "react";
import { Row, Col, Form, Button, Card, Spinner } from "react-bootstrap";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import { normalizeAttachments } from "./expenseUtils";
import { attachModuleGuard, AccessDeniedModal } from "../../../utils/moduleGuard";

// /expense-management → "expense_management". Match on module_key only.
// This tab is reachable by URL (/expense-management/add-expense) without going through
// the gated edit handler in ExpenseManagementMain, so it re-checks at its own submit.
const EXPENSE_MODULE = ["expense_management"];

const CREATE_EXPENSE = gql`
  mutation ($input: ExpenseInput!) {
    createExpense(input: $input) {
      id
    }
  }
`;

const UPDATE_EXPENSE = gql`
  mutation ($input: ExpenseInput!) {
    updateExpense(input: $input) {
      id
    }
  }
`;

const THEME = {
  primary: "#6366f1",
  success: "#10b981",
  text: "#0f172a",
  textMuted: "#64748b",
  border: "#e2e8f0",
  bgLight: "#f8fafc",
};

class AddExpense extends Component {
  constructor(props) {
    super(props);
    const { editItem } = props;
    this.state = {
      formData: {
        expense_head_id: editItem ? editItem.expense_head_id : "",
        expense_category_id: editItem ? editItem.expense_category_id : "",
        expense_date: editItem ? new Date(isNaN(editItem.expense_date) ? editItem.expense_date : Number(editItem.expense_date)).toLocaleDateString("en-CA") : new Date().toLocaleDateString("en-CA"),
        expense_vendor_id: editItem ? (editItem.expense_vendor_id || "") : "",
        amount: editItem ? editItem.amount : "",
        tax_amount: editItem ? editItem.tax_amount : 0,
        discount_amount: editItem ? editItem.discount_amount : 0,
        final_amount: editItem ? editItem.final_amount : 0,
        payment_mode_id: editItem ? editItem.payment_mode_id : "",
        reference_no: editItem ? editItem.reference_no : "",
        description: editItem ? editItem.description : "",
      },
      saving: false,
      uploading: false,
      attachments: editItem && editItem.attachments ? normalizeAttachments(editItem.attachments) : [],
      errors: {},
      warnings: {},
      accessDeniedShow: false,
    };

    this.guard = attachModuleGuard(this, EXPENSE_MODULE);
  }

  hasExceededDigitLimit = (value, maxChars = 8) => {
    if (value === undefined || value === null || value === "") return false;
    const stringVal = String(value);
    const intPart = stringVal.includes('.') ? stringVal.split('.')[0] : stringVal;
    return intPart.length > maxChars;
  };

  recalculateFinal = () => {
    const amount   = parseFloat(this.state.formData.amount)          || 0;
    const tax      = parseFloat(this.state.formData.tax_amount)      || 0;
    const discount = parseFloat(this.state.formData.discount_amount) || 0;
    const final    = parseFloat((amount + tax - discount).toFixed(2));
    
    const warnings = { ...this.state.warnings };
    const errors   = { ...this.state.errors };

    // Tax warnings
    if (tax > amount && amount > 0) {
      warnings.tax_amount = "Tax amount is greater than the expense amount. Please verify.";
    } else if (tax > amount * 0.5 && amount > 0) {
      warnings.tax_amount = "Tax is more than 50% of the amount. Please verify.";
    } else {
      delete warnings.tax_amount;
    }

    // Discount warnings and errors
    if (discount >= amount && amount > 0) {
      errors.discount_amount = "Discount cannot be equal to or greater than the amount";
      delete warnings.discount_amount;
    } else if (discount > amount * 0.5 && amount > 0) {
      delete errors.discount_amount;
      warnings.discount_amount = "Discount is more than 50% of the amount. Please verify.";
    } else {
      delete errors.discount_amount;
      delete warnings.discount_amount;
    }

    // Final amount check
    if (final <= 0 && amount > 0) {
      errors.final_amount = "Final amount must be greater than 0";
    } else {
      delete errors.final_amount;
    }

    this.setState(prev => ({
      formData: {
        ...prev.formData,
        final_amount: final > 0 ? final : 0
      },
      warnings,
      errors
    }));
  };

  validateField = (name, value) => {
    const errors = { ...this.state.errors };
    const warnings = { ...this.state.warnings };

    if (name === "expense_head_id") {
      if (!value || value === "") {
        errors.expense_head_id = "Please select a valid option";
      } else {
        delete errors.expense_head_id;
      }
    }

    if (name === "expense_category_id") {
      if (!value || value === "") {
        errors.expense_category_id = "Please select a valid option";
      } else {
        delete errors.expense_category_id;
      }
    }

    if (name === "payment_mode_id") {
      if (!value || value === "") {
        errors.payment_mode_id = "Please select a valid option";
      } else {
        delete errors.payment_mode_id;
      }
    }

    if (name === "expense_date") {
      if (!value) {
        errors.expense_date = "Date is required";
      } else {
        const d = new Date(value);
        const now = new Date();
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(now.getFullYear() - 1);
        const tomorrow = new Date();
        tomorrow.setDate(now.getDate() + 1);
        if (d < oneYearAgo) {
          errors.expense_date = "Date cannot be older than 1 year";
        } else if (d > tomorrow) {
          errors.expense_date = "Future date not allowed beyond tomorrow";
        } else {
          delete errors.expense_date;
        }
      }
    }

    if (name === "amount") {
      const amt = parseFloat(value);
      if (!value || isNaN(amt)) {
        errors.amount = "Amount is required";
      } else if (amt <= 0) {
        errors.amount = "Amount must be greater than 0";
      } else if (this.hasExceededDigitLimit(value, 10)) {
        errors.amount = "Amount cannot exceed 10 characters";
      } else if (!/^\d+(\.\d{1,2})?$/.test(String(value))) {
        errors.amount = "Maximum 2 decimal places allowed";
      } else {
        delete errors.amount;
      }
    }

    if (name === "tax_amount") {
      const tax = parseFloat(value) || 0;
      if (tax < 0) {
        errors.tax_amount = "Tax cannot be negative";
      } else if (this.hasExceededDigitLimit(value, 10)) {
        errors.tax_amount = "Tax amount cannot exceed 10 characters";
      } else if (value !== "" && !/^\d+(\.\d{1,2})?$/.test(String(value))) {
        errors.tax_amount = "Maximum 2 decimal places allowed";
      } else {
        delete errors.tax_amount;
      }
    }

    if (name === "discount_amount") {
      const disc = parseFloat(value) || 0;
      const amt = parseFloat(this.state.formData.amount) || 0;
      if (disc < 0) {
        errors.discount_amount = "Discount cannot be negative";
      } else if (this.hasExceededDigitLimit(value, 10)) {
        errors.discount_amount = "Discount amount cannot exceed 10 characters";
      } else if (value !== "" && !/^\d+(\.\d{1,2})?$/.test(String(value))) {
        errors.discount_amount = "Maximum 2 decimal places allowed";
      } else if (disc >= amt && amt > 0) {
        errors.discount_amount = "Discount cannot be equal to or greater than the amount";
      } else {
        delete errors.discount_amount;
      }
    }

    if (name === "reference_no") {
      if (value && value.length > 20) {
        errors.reference_no = "Reference number cannot exceed 20 characters";
      } else if (value && !/^[a-zA-Z0-9\-\/]*$/.test(value)) {
        errors.reference_no = "Only letters, numbers, hyphens, slashes allowed";
      } else {
        delete errors.reference_no;
      }
    }

    if (name === "description") {
      if (value && value.length > 500) {
        errors.description = "Description cannot exceed 500 characters";
      } else {
        delete errors.description;
      }
    }

    this.setState({ errors, warnings });
  };

  validateForm = () => {
    const errors = {};
    const { formData, attachments } = this.state;
    const {
      expense_head_id, expense_category_id, expense_date,
      amount, tax_amount, discount_amount, final_amount,
      payment_mode_id, reference_no, description
    } = formData;

    if (!expense_head_id || expense_head_id === "")
      errors.expense_head_id = "Select an expense head";

    if (!expense_category_id || expense_category_id === "")
      errors.expense_category_id = "Select a category";

    if (!expense_date)
      errors.expense_date = "Date is required";
    else {
      const d = new Date(expense_date);
      const now = new Date();
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(now.getFullYear() - 1);
      const tomorrow = new Date();
      tomorrow.setDate(now.getDate() + 1);
      if (d < oneYearAgo) errors.expense_date = "Date cannot be older than 1 year";
      if (d > tomorrow)   errors.expense_date = "Future date not allowed beyond tomorrow";
    }

    const amt = parseFloat(amount);
    if (!amount || isNaN(amt))
      errors.amount = "Amount is required";
    else if (amt <= 0)
      errors.amount = "Amount must be greater than 0";
    else if (this.hasExceededDigitLimit(amount, 10))
      errors.amount = "Amount cannot exceed 10 characters";
    else if (!/^\d+(\.\d{1,2})?$/.test(String(amount)))
      errors.amount = "Maximum 2 decimal places allowed";

    const tax = parseFloat(tax_amount) || 0;
    if (tax < 0)
      errors.tax_amount = "Tax cannot be negative";
    else if (this.hasExceededDigitLimit(tax, 10))
      errors.tax_amount = "Tax amount cannot exceed 10 characters";
    else if (tax_amount !== "" && !/^\d+(\.\d{1,2})?$/.test(String(tax_amount)))
      errors.tax_amount = "Maximum 2 decimal places allowed";

    const disc = parseFloat(discount_amount) || 0;
    if (disc < 0)
      errors.discount_amount = "Discount cannot be negative";
    else if (this.hasExceededDigitLimit(disc, 10))
      errors.discount_amount = "Discount amount cannot exceed 10 characters";
    else if (discount_amount !== "" && !/^\d+(\.\d{1,2})?$/.test(String(discount_amount)))
      errors.discount_amount = "Maximum 2 decimal places allowed";
    else if (disc >= amt && amt > 0)
      errors.discount_amount = "Discount cannot be equal to or greater than the amount";

    if (!final_amount || parseFloat(final_amount) <= 0)
      errors.final_amount = "Final amount must be greater than 0";

    if (!payment_mode_id || payment_mode_id === "")
      errors.payment_mode_id = "Select a payment mode";

    if (reference_no) {
      if (reference_no.length > 20) {
        errors.reference_no = "Reference number cannot exceed 20 characters";
      } else if (!/^[a-zA-Z0-9\-\/]*$/.test(reference_no)) {
        errors.reference_no = "Only letters, numbers, hyphens, slashes allowed";
      }
    }

    if (description && description.length > 500)
      errors.description = "Description cannot exceed 500 characters";

    if (attachments && attachments.length > 5)
      errors.attachments = "Maximum 5 attachments allowed";

    return errors;
  };

  // The receipt picker. It only stages File objects locally (the upload mutation fires
  // inside handleSubmit), but it's part of the write flow, so a denied role is stopped
  // here rather than being allowed to fill in a form they can't submit.
  handleFileChange = (e) => {
    if (!this.guard.requireEdit()) {
      e.target.value = "";
      return;
    }

    const files = e.target.files;
    if (!files || files.length === 0) return;

    const currentCount = this.state.attachments.length;
    const incomingCount = files.length;
    const errors = { ...this.state.errors };

    if (currentCount + incomingCount > 5) {
      errors.attachments = "Maximum 5 attachments allowed";
      this.setState({ errors });
      return;
    }

    const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'xlsx', 'csv'];
    const maxSizeBytes = 2 * 1024 * 1024; // 2MB
    const newAttachments = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(ext)) {
        errors.attachments = "Only PDF, JPG, PNG, XLSX, CSV files allowed";
        this.setState({ errors });
        return;
      }

      if (file.size > maxSizeBytes) {
        errors.attachments = "Each file must be under 2MB";
        this.setState({ errors });
        return;
      }
    }

    delete errors.attachments;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const localUrl = URL.createObjectURL(file);
      newAttachments.push({
        name: file.name,
        url: localUrl,
        fileObject: file,
        isLocal: true
      });
    }

    this.setState(prev => ({
      attachments: [...prev.attachments, ...newAttachments],
      errors
    }));
  };

  removeAttachment = (index) => {
    const fileToRemove = this.state.attachments[index];
    if (fileToRemove && fileToRemove.isLocal && fileToRemove.url) {
      URL.revokeObjectURL(fileToRemove.url);
    }
    this.setState(
      prev => {
        const nextAttachments = prev.attachments.filter((_, i) => i !== index);
        const errors = { ...prev.errors };
        if (nextAttachments.length <= 5) {
          delete errors.attachments;
        }
        return {
          attachments: nextAttachments,
          errors
        };
      }
    );
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    if (["amount", "tax_amount", "discount_amount"].includes(name)) {
      if (value && String(value).split('.')[0].length > 10) {
        return;
      }
    }
    this.setState(
      prev => ({
        formData: {
          ...prev.formData,
          [name]: value,
          // Changing the Category clears the dependent Sub Category
          ...(name === "expense_head_id" ? { expense_category_id: "" } : {}),
        }
      }),
      () => {
        this.validateField(name, value);
        if (["amount", "tax_amount", "discount_amount"].includes(name)) {
          this.recalculateFinal();
        }
      }
    );
  };

  handleBlur = (e) => {
    const { name, value } = e.target;
    if (["amount", "tax_amount", "discount_amount"].includes(name) && value !== "") {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        this.setState(
          prev => ({ formData: { ...prev.formData, [name]: parsed.toFixed(2) } }),
          () => {
            this.validateField(name, parsed.toFixed(2));
            this.recalculateFinal();
          }
        );
      }
    }
  };

  // The single save for create + edit + receipt upload. Last gate before any of them
  // leaves the client, and the one that matters most: /expense-management/add-expense can
  // be typed straight into the address bar, mounting this form without ever passing
  // through the gated handleEdit in ExpenseManagementMain.
  handleSubmit = async (e) => {
    e.preventDefault();

    if (!this.guard.requireEdit()) return;

    const errors = this.validateForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const { client, onSuccess } = this.props;
    const { formData, attachments } = this.state;
    const userId = parseInt(Cookies.get("id") || Cookies.get("user_id") || "0");
    const instituteId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || Cookies.get("branchid") || "0");

    this.setState({ saving: true });
    try {
      // 1. Process and upload any new local files
      const uploadedAttachments = [];
      for (const item of attachments) {
        if (item.isLocal && item.fileObject) {
          const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(item.fileObject);
          });

          const response = await client.mutate({
            mutation: gql`
              mutation FileUpload($params: FileUploadInput!) {
                fileUpload(params: $params) {
                  uuid
                  file_url
                  file_name
                }
              }
            `,
            variables: {
              params: {
                category: "expense",
                client_id: instituteId,
                file: [{ img: base64Data, filename: item.name }]
              }
            }
          });

          const result = response.data.fileUpload[0];
          if (result) {
            uploadedAttachments.push({
              name: result.file_name,
              uuid: result.uuid
            });
          }
        } else {
          // Already uploaded previous assets
          uploadedAttachments.push({
            name: item.name,
            uuid: item.uuid || "",
            ...(item.uuid ? {} : { url: item.url }) // Fallback to URL only if legacy record lacks a UUID
          });
        }
      }

      const { editItem } = this.props;

      // 2. Dispatch final Create or Update Mutation
      const input = {
        id: editItem ? editItem.id : undefined,
        expense_head_id: parseInt(formData.expense_head_id),
        expense_category_id: parseInt(formData.expense_category_id),
        expense_date: formData.expense_date,
        expense_vendor_id: formData.expense_vendor_id ? parseInt(formData.expense_vendor_id) : null,
        payment_mode_id: formData.payment_mode_id ? parseInt(formData.payment_mode_id) : null,
        amount: parseFloat(formData.amount),
        tax_amount: parseFloat(formData.tax_amount),
        discount_amount: parseFloat(formData.discount_amount),
        final_amount: parseFloat(formData.final_amount),
        reference_no: formData.reference_no,
        description: formData.description,
        attachments: JSON.stringify(uploadedAttachments),
        institute_id: instituteId,
        branch_id: branchId,
        inserted_by: userId
      };

      if (editItem) {
        await client.mutate({
          mutation: UPDATE_EXPENSE,
          variables: { input }
        });
        alert("Expense updated successfully!");
      } else {
        await client.mutate({
          mutation: CREATE_EXPENSE,
          variables: { input }
        });
        alert("Expense added successfully!");
      }
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error saving expense:", err);
      alert("Failed to save expense: " + err.message);
    }
    this.setState({ saving: false });
  };

  render() {
    // No full-page view card here on purpose: per product decision the Add Expense form
    // stays fully rendered and every button visible + clickable regardless of access.
    // Permission is validated on the action instead — handleFileChange / handleSubmit
    // call requireEdit() and pop the Access Denied modal, so nothing is hidden or
    // redirected. (Route-level is_view is still enforced upstream in App.js / the
    // ExpenseManagementMain container for users who lack the module entirely.)
    const { formData, saving, errors, warnings } = this.state;
    const { configs } = this.props;

    const labelStyle = { fontSize: "12px", fontWeight: "600", color: THEME.textMuted, marginBottom: "6px", display: "block" };
    const inputStyle = { border: `1px solid ${THEME.border}`, borderRadius: "8px", padding: "11px 14px", fontSize: "14px", color: THEME.text, transition: "border-color 0.15s ease-in-out" };

    const hasErrors = errors && Object.keys(errors).length > 0;
    const finalVal = parseFloat(formData.final_amount) || 0;

    return (
      <div style={{ padding: "8px 0" }}>
        <div>
          <Form onSubmit={this.handleSubmit}>
            <Row>
              {/* Left Column - Main Details Form (Col lg={8} md={7}) */}
              <Col lg={8} md={7} className="pr-lg-5" style={{ borderRight: `1px solid ${THEME.border}` }}>
                <div className="mb-4 d-flex align-items-center">
                  <div style={{ background: "#eff6ff", color: THEME.primary, width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContext: "center", marginRight: "12px", justifyContent: "center" }}>
                    <i className="fal fa-file-invoice" style={{ fontSize: "16px" }} />
                  </div>
                  <h6 style={{ margin: 0, fontWeight: "800", color: THEME.text, fontSize: "15px" }}>General Information</h6>
                </div>
                
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label style={labelStyle}>Category *</Form.Label>
                    <Form.Control as="select" name="expense_head_id" style={inputStyle} value={formData.expense_head_id} onChange={this.handleChange} required>
                      <option value="">Select Category</option>
                      {configs?.getExpenseHeads?.map(h => <option key={h.id} value={h.id}>{h.expense_head}</option>)}
                    </Form.Control>
                    {errors?.expense_head_id && (
                      <p className="field-error" style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                        ⛔ {errors.expense_head_id}
                      </p>
                    )}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label style={labelStyle}>Sub Category *</Form.Label>
                    <Form.Control as="select" name="expense_category_id" style={inputStyle} value={formData.expense_category_id} onChange={this.handleChange} disabled={!formData.expense_head_id} required>
                      <option value="">{formData.expense_head_id ? "Select Sub Category" : "Select a Category first"}</option>
                      {configs?.getExpenseCategories
                        ?.filter(c => String(c.expense_head_id) === String(formData.expense_head_id))
                        .map(c => <option key={c.id} value={c.id}>{c.expense_categories}</option>)}
                    </Form.Control>
                    {errors?.expense_category_id && (
                      <p className="field-error" style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                        ⛔ {errors.expense_category_id}
                      </p>
                    )}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label style={labelStyle}>Date *</Form.Label>
                    <Form.Control type="date" name="expense_date" style={inputStyle} value={formData.expense_date} onChange={this.handleChange} required />
                    {errors?.expense_date && (
                      <p className="field-error" style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                        ⛔ {errors.expense_date}
                      </p>
                    )}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label style={labelStyle}>Vendor</Form.Label>
                    <Form.Control as="select" name="expense_vendor_id" style={inputStyle} value={formData.expense_vendor_id} onChange={this.handleChange}>
                      <option value="">Select Vendor</option>
                      {configs?.getExpenseVendors?.map(v => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
                    </Form.Control>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label style={labelStyle}>Payment Mode *</Form.Label>
                    <Form.Control as="select" name="payment_mode_id" style={inputStyle} value={formData.payment_mode_id} onChange={this.handleChange} required>
                      <option value="">Select Mode</option>
                      {configs?.getPaymentModes?.map(p => <option key={p.id} value={p.id}>{p.payment_mode}</option>)}
                    </Form.Control>
                    {errors?.payment_mode_id && (
                      <p className="field-error" style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                        ⛔ {errors.payment_mode_id}
                      </p>
                    )}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label style={labelStyle}>Reference / Bill No</Form.Label>
                    <Form.Control type="text" name="reference_no" maxLength={20} placeholder="e.g. INV-2024-001" style={inputStyle} value={formData.reference_no} onChange={this.handleChange} />
                    {errors?.reference_no && (
                      <p className="field-error" style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                        ⛔ {errors.reference_no}
                      </p>
                    )}
                  </Col>
                </Row>

                <div className="mt-4 mb-4 d-flex align-items-center">
                  <div style={{ background: "#eff6ff", color: THEME.primary, width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContext: "center", marginRight: "12px", justifyContent: "center" }}>
                    <i className="fal fa-calculator" style={{ fontSize: "16px" }} />
                  </div>
                  <h6 style={{ margin: 0, fontWeight: "800", color: THEME.text, fontSize: "15px" }}>Financial Details</h6>
                </div>

                <Row>
                  <Col md={4} className="mb-3">
                    <Form.Label style={labelStyle}>Base Amount *</Form.Label>
                    <Form.Control type="number" step="any" name="amount" placeholder="0.00" style={inputStyle} value={formData.amount} onChange={this.handleChange} onBlur={this.handleBlur} onWheel={(e) => e.target.blur()} required />
                    {errors?.amount && (
                      <p className="field-error" style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                        ⛔ {errors.amount}
                      </p>
                    )}
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Label style={labelStyle}>Tax Amount</Form.Label>
                    <Form.Control type="number" step="any" name="tax_amount" placeholder="0.00" style={inputStyle} value={formData.tax_amount} onChange={this.handleChange} onBlur={this.handleBlur} onWheel={(e) => e.target.blur()} />
                    {errors?.tax_amount && (
                      <p className="field-error" style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                        ⛔ {errors.tax_amount}
                      </p>
                    )}
                    {warnings?.tax_amount && (
                      <p className="field-warning" style={{ color: "#e65c00", fontSize: 11, marginTop: 4 }}>
                        ⚠️ {warnings.tax_amount}
                      </p>
                    )}
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Label style={labelStyle}>Discount Amount</Form.Label>
                    <Form.Control type="number" step="any" name="discount_amount" placeholder="0.00" style={inputStyle} value={formData.discount_amount} onChange={this.handleChange} onBlur={this.handleBlur} onWheel={(e) => e.target.blur()} />
                    {errors?.discount_amount && (
                      <p className="field-error" style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                        ⛔ {errors.discount_amount}
                      </p>
                    )}
                    {warnings?.discount_amount && (
                      <p className="field-warning" style={{ color: "#e65c00", fontSize: 11, marginTop: 4 }}>
                        ⚠️ {warnings.discount_amount}
                      </p>
                    )}
                  </Col>
                  
                  <Col md={12} className="mb-3">
                    <div style={{
                      background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                      borderRadius: "10px",
                      padding: "16px 20px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1px solid #bfdbfe",
                      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.02)"
                    }}>
                      <span style={{ fontSize: "13px", fontWeight: "700", color: "#1e40af" }}>Total Final Amount</span>
                      <span style={{ fontSize: "22px", fontWeight: "900", color: "#1d4ed8" }}>
                        ₹ {finalVal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    {errors?.final_amount && (
                      <p className="field-error" style={{ color: "red", fontSize: 11, marginTop: 4 }}>
                        ⛔ {errors.final_amount}
                      </p>
                    )}
                  </Col>

                  <Col md={12} className="mb-3">
                    <Form.Label style={labelStyle}>Description / Remarks</Form.Label>
                    <Form.Control as="textarea" rows={3} name="description" maxLength={500} placeholder="Enter details or comments about this expense..." style={inputStyle} value={formData.description} onChange={this.handleChange} />
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <div>
                        {errors?.description && (
                          <p className="field-error" style={{ color: "red", fontSize: 11, margin: 0 }}>
                            ⛔ {errors.description}
                          </p>
                        )}
                      </div>
                      <small style={{ color: (formData.description || "").length > 450 ? ((formData.description || "").length === 500 ? "red" : "orange") : "#888", fontWeight: "600" }}>
                        {(formData.description || "").length} / 500
                      </small>
                    </div>
                  </Col>
                </Row>
              </Col>

              {/* Right Column - Attachment Uploader & Preview (Col lg={4} md={5}) */}
              <Col lg={4} md={5} className="pl-lg-5 mt-4 mt-md-0">
                <div className="mb-4 d-flex align-items-center">
                  <div style={{ background: "#eff6ff", color: THEME.primary, width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContext: "center", marginRight: "12px", justifyContent: "center" }}>
                    <i className="fal fa-paperclip" style={{ fontSize: "16px" }} />
                  </div>
                  <h6 style={{ margin: 0, fontWeight: "800", color: THEME.text, fontSize: "15px" }}>Attachments</h6>
                </div>

                {/* Modern File Dropzone area */}
                <div 
                  style={{
                    border: `2px dashed ${errors?.attachments ? "red" : THEME.border}`,
                    borderRadius: "12px",
                    padding: "36px 20px",
                    textAlign: "center",
                    background: THEME.bgLight,
                    cursor: "pointer",
                    position: "relative",
                    transition: "all 0.2s ease-in-out",
                    marginBottom: "24px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = THEME.primary; e.currentTarget.style.background = "#eff6ff"; }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = errors?.attachments ? "red" : THEME.border; e.currentTarget.style.background = THEME.bgLight; }}
                >
                  <i className="fal fa-cloud-upload" style={{ fontSize: "40px", color: errors?.attachments ? "red" : THEME.primary, marginBottom: "14px" }} />
                  <span style={{ fontSize: "13px", fontWeight: "700", color: THEME.text, display: "block" }}>Upload Expense Invoice</span>
                  <span style={{ fontSize: "11px", color: THEME.textMuted, marginTop: "5px" }}>Drag & drop files or click to browse</span>
                  
                  <input
                    type="file"
                    multiple
                    onChange={this.handleFileChange}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
                  />
                </div>
                {errors?.attachments && (
                  <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: -12, marginBottom: 16, textAlign: "center", fontWeight: "600" }}>
                    ⛔ {errors.attachments}
                  </p>
                )}

                {/* Previews Panel Stack */}
                <div className="mb-2">
                  <span style={labelStyle}>Uploaded Document ({this.state.attachments.length})</span>
                </div>
                
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxHeight: "280px", overflowY: "auto", paddingRight: "4px" }}>
                  {this.state.attachments.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "30px 15px", background: THEME.bgLight, borderRadius: "10px", border: `1px solid ${THEME.border}` }}>
                      <i className="fal fa-file-invoice" style={{ fontSize: "28px", color: THEME.textMuted, marginBottom: "8px", display: "block" }} />
                      <span style={{ fontSize: "12px", color: THEME.textMuted }}>No receipts uploaded yet.<br />PDF, JPG, PNG, XLSX, CSV allowed.</span>
                    </div>
                  ) : (
                    this.state.attachments.map((file, idx) => {
                      const isImage = file.name.match(/\.(jpg|jpeg|png|gif)$/i) || file.url.match(/\.(jpg|jpeg|png|gif)/i);
                      return (
                        <div key={idx} style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px 12px",
                          borderRadius: "10px",
                          border: `1px solid ${THEME.border}`,
                          background: "#ffffff",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
                        }}>
                          {/* Left Icon or Thumbnail */}
                          <div style={{
                            width: "38px",
                            height: "38px",
                            borderRadius: "8px",
                            marginRight: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: isImage ? `url(${file.url}) center/cover no-repeat` : "#fee2e2",
                            border: `1px solid ${THEME.border}`,
                            flexShrink: 0
                          }}>
                            {!isImage && <i className="fas fa-file-pdf" style={{ fontSize: "18px", color: "#dc2626" }} />}
                          </div>

                          {/* File Label Details */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <a 
                              href={file.url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              style={{ display: "block", fontSize: "13px", fontWeight: "700", color: THEME.text, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              title="Click to view file"
                            >
                              {file.name}
                            </a>
                            <span style={{ fontSize: "11px", color: THEME.textMuted }}>Invoice attachment</span>
                          </div>

                          {/* Action Icon buttons */}
                          <button 
                            type="button" 
                            onClick={() => this.removeAttachment(idx)}
                            style={{ background: "none", border: "none", color: THEME.danger, padding: "6px", cursor: "pointer", transition: "color 0.15s ease-in-out" }}
                            title="Remove file"
                          >
                            <i className="fal fa-trash-alt" />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </Col>
            </Row>

            {/* Bottom Actions Row */}
            <div className="d-flex justify-content-end gap-3 mt-5 pt-4" style={{ borderTop: `1px solid ${THEME.border}` }}>
              <Button variant="light" className="px-4 font-weight-bold" disabled={saving} style={{ padding: "10px 24px", borderRadius: "8px", fontSize: "13px" }}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" className="px-5 font-weight-bold shadow-sm" disabled={saving || hasErrors} style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", border: "none", padding: "10px 32px", borderRadius: "8px", fontSize: "13px" }}>
                {saving ? <><Spinner size="sm" animation="border" className="mr-2" /> Saving...</> : "Save Expense"}
              </Button>
            </div>
          </Form>
        </div>

        {/* Shown instead of saving the expense / staging a receipt when is_edit is
            missing. This tab has no delete surface. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />
      </div>
    );
  }
}

export default AddExpense;
